﻿import React from 'react';
import { Box, Typography, Select, MenuItem, TextField, Button, FormControl, InputLabel } from '@mui/material';

const MetadataEditor = ({ annotations, setAnnotations, selectedId, setSelectedId }) => {
  // Common label options: customize these as needed.
  const roofLabels = ['flat', 'pitched', 'other']; // Removed hip, gable
  const obstructionLabels = ['vent', 'chimney', 'skylight', 'antenna', 'solar_panel', 'other'];

  if (selectedId == null) {
    return (
      <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 1, minWidth: 250 }}>
        <Typography variant="subtitle1">No Box Selected</Typography>
        <Typography variant="body2">Select a bounding box on the image to edit its label or tilt. </Typography>
      </Box>
    );
  }

  const annIndex = annotations.findIndex((item) => item.id === selectedId);

  if (annIndex === -1) {
    console.warn(`Selected ID ${selectedId} not found in annotations.`);
    setSelectedId(null); // Deselect if not found
    return (
      <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 1, minWidth: 250 }}>
        <Typography variant="subtitle1">Error</Typography>
        <Typography variant="body2">Selected annotation not found. Please select another.</Typography>
      </Box>
    );
  }

  const currentAnn = annotations[annIndex];

  // Decide which list of labels to show.
  const labelOptions =
    currentAnn.annotationType === 'roof' ? roofLabels : obstructionLabels;

  // Update the common setter for annotation properties
  const setAnnotationProperty = (property, value) => {
    setAnnotations(prevAnnotations => {
      const updated = [...prevAnnotations];
      if (updated[annIndex]) {
        updated[annIndex] = {
          ...currentAnn,
          [property]: value,
        };
      }
      return updated;
    });
  };

  // Update the label in the parent's nnotations state.
  const setAnnotationLabel = (newLabel) => {
    setAnnotationProperty('label', newLabel || ''); // Ensure label is always a string
  };

  const setAnnotationTilt = (newTilt) => {
    setAnnotationProperty('tilt_angle', newTilt === '' ? null : parseFloat(newTilt));
  };

  // Handle <select> changes.
  const handleSelectChange = (e) => {
    setAnnotationLabel(e.target.value);
  };

  // Handle text input changes (free-form label).
  const handleInputChange = (e) => {
    setAnnotationLabel(e.target.value);
  };
  const handleTiltChange = (e) => {
    const value = e.target.value;
    // Validate numeric input using a regular expression. Only allow digits and a single decimal point.
    if (/^(\d+(\.\d*)?)$|^$/.test(value)) {
      setAnnotationTilt(value);
    }
  };

  const handleDelete = () => {
    setAnnotations(prevAnnotations => prevAnnotations.filter((a) => a.id !== selectedId));
    setSelectedId(null); // Deselect after deleting
  };

  const annotationTypeName =
    currentAnn.annotationType === 'roof' ? 'Roof Field' : 'Obstruction';

  return (
    <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 1, minWidth: 250, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">Edit {annotationTypeName}</Typography>
      <Typography variant="caption" >ID: {currentAnn.id}</Typography>

      {/* Predefined Labels Dropdown */}
      <FormControl fullWidth>
        <InputLabel id="predefined-label-select-label">Predefined Label</InputLabel>
        <Select
          labelId="predefined-label-select-label"
          id="predefined-label-select"
          value={labelOptions.includes(currentAnn.label) ? currentAnn.label : ''}
          label="Predefined Label"
          onChange={handleSelectChange}
        >
          <MenuItem value="">
            <em>(None/Custom)</em>
          </MenuItem>
          {labelOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Free-form Text Input */}
      <TextField
        id="freeform-label-input"
        label="Custom Label"
        variant="outlined"
        value={currentAnn.label || ''}
        onChange={handleInputChange}
        fullWidth
      />

      {currentAnn.annotationType === 'roof' && (
        <TextField
          label="Tilt Angle (degrees)"
          type="number"
          variant="outlined"
          fullWidth
          value={currentAnn.tilt_angle !== null ? currentAnn.tilt_angle : ''}
          onChange={handleTiltChange}
          inputProps={{
            step: 1, // Allow integer values
            min: 0,
            max: 90,
          }}
          helperText="Enter the tilt angle in degrees. Leave empty for no tilt."
        />
      )}

      {/* Delete Button */}
      <Button
        variant="contained"
        color="error"
        onClick={handleDelete}
        sx={{ mt: 'auto' }} // Push to bottom if needed
      >
        Delete This Box
      </Button>
    </Box>
  );
};

export default MetadataEditor;